import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Alert,
} from "@mui/material";
import { Mic, Stop, Send, Translate, Create, PauseCircleOutline, PlayCircleOutline } from "@mui/icons-material";
import { toast } from "react-toastify";
// htlifeAxios is the dedicated instance for HTLife service calls.
// It auto-attaches Authorization + Org-Code + App-Mode and handles
// token refresh on 401 — no manual token management needed here.
import htlifeAxios from "../../utils/htlifeAxios";
import DoctorSlotsDisplay from "./DoctorSlotsDisplay";
import ErrorBoundary from "./ErrorBoundary";
import "./VoiceAssistant.scss";

const LANGUAGES = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
    { code: "mix", name: "Mixed Languages" },
];

// const VoiceAssistant = ({ patientId = "PAT-848", orgCode, appMode, onClose }) => {
const VoiceAssistant = ({ patient, orgCode, appMode, onClose, authToken, onGoToMyAppointments, onResultsChange }) => {
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [inputMode, setInputMode] = useState("voice"); // "voice" | "text"
    const [textInput, setTextInput] = useState("");
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [assessmentData, setAssessmentData] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [showDoctors, setShowDoctors] = useState(false);

    const { patientUsername, patientPassword } = patient;

    const [isPlaying, setIsPlaying] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioPlayerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [audioUrl]);

    useEffect(() => {
        onResultsChange?.(showResults);
    }, [showResults, onResultsChange]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (error) {
            console.error("Error accessing microphone:", error);
            toast.error("Unable to access microphone. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const handleSendAssessment = async () => {
        if (inputMode === "voice" && !audioBlob) {
            toast.error("Please record your voice first");
            return;
        }
        if (inputMode === "text" && textInput.trim().length < 10) {
            toast.error("Please describe your condition (minimum 10 characters)");
            return;
        }

        setIsProcessing(true);
        try {
            const resolvedUsername = patientUsername || sessionStorage.getItem("userName") || "";

            // Only seed sessionStorage if it's empty — htlifeAxios may have already
            // refreshed and stored a newer token. Overwriting would re-introduce an
            // expired token and cause the next call to fail.
            if (authToken && !sessionStorage.getItem("htlifeAccessToken")) {
                sessionStorage.setItem("htlifeAccessToken", authToken);
            }

            let response;
            if (inputMode === "text") {
                const textFormData = new FormData();
                textFormData.append("complaint_text", textInput.trim());
                textFormData.append("username", patientUsername);
                textFormData.append("password", patientPassword);
                response = await htlifeAxios.post("patient_text_assessment/", textFormData);
            } else {
                // Voice assessment goes to HTLife service with multipart FormData.
                // htlifeAxios interceptor attaches Authorization + Org-Code + App-Mode
                // and will silently refresh the token on 401 before retrying.
                const formData = new FormData();
                formData.append("file", audioBlob, "recording.webm");
                formData.append("language", selectedLanguage);
                formData.append("username", patientUsername);
                formData.append("password", patientPassword);

                const headers = {};
                if (resolvedUsername) headers["Username"] = resolvedUsername;
                response = await htlifeAxios.post("patient_voice_assessment/", formData, { headers });
            }

            if (response?.data?.status === "Success") {
                console.log("response?.data?.data", response?.data?.data);
                // Set state directly - ErrorBoundary will catch any rendering errors
                setAssessmentData(response?.data || {});
                setShowResults(true);
                // Defer showing doctors section to avoid theme errors
                setTimeout(() => {
                    setShowDoctors(true);
                }, 100);
                toast.success("Assessment completed successfully!");
            } else {
                toast.error(response?.data?.message || "Failed to get assessment. Please try again.");
            }
        } catch (error) {
            console.error("Error sending voice assessment:", error);
            const errorMessage = error?.response?.data?.message ||
                error?.message ||
                "Unable to process voice assessment. Please try again.";
            toast.error(errorMessage);
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        if (audioPlayerRef.current) audioPlayerRef.current.pause();
        setIsPlaying(false);
        setAudioBlob(null);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
        setAssessmentData(null);
        setShowResults(false);
        setShowDoctors(false);
        setRecordingTime(0);
        setTextInput("");
    };

    const handleNewRecording = () => {
        handleReset();
    };

    const isSubmitDisabled = isProcessing ||
        (inputMode === "voice" ? !audioBlob : textInput.trim().length < 10);

    return (
        <Box className="voice-assistant-container">
            <Card className="voice-assistant-card" style={{ borderRadius: 18, boxShadow: "0 6px 32px rgba(14,63,102,0.10)", border: "1px solid #e8f0fa" }}>
                <CardContent style={{ padding: "28px 30px" }}>

                    {!showResults ? (
                        <>
                            {/* ── Preferred Language ── */}
                            <div style={{ marginBottom: 22 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                    <Translate style={{ fontSize: 20, color: "#0E3F66" }} />
                                    <Typography variant="subtitle2" fontWeight={700} color="#0d1b3d">
                                        Preferred Language
                                    </Typography>
                                </div>
                                <Select
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                    fullWidth
                                    size="small"
                                    disabled={isRecording || isProcessing}
                                    style={{ borderRadius: 10, background: "#fff" }}
                                >
                                    {LANGUAGES.map((lang) => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>

                            {/* ── Mode Toggle ── */}
                            <div style={{
                                display: "flex",
                                border: "1px solid #dde6f0",
                                borderRadius: 12,
                                overflow: "hidden",
                                marginBottom: 22,
                            }}>
                                {[
                                    { key: "voice", Icon: Mic, label: "Record voice" },
                                    { key: "text", Icon: Create, label: "Write it" },
                                ].map(({ key, Icon, label }, i) => (
                                    <button
                                        key={key}
                                        onClick={() => { if (!isRecording && !isProcessing) setInputMode(key); }}
                                        style={{
                                            flex: 1,
                                            padding: "11px 0",
                                            border: "none",
                                            borderRight: i === 0 ? "1px solid #dde6f0" : "none",
                                            background: inputMode === key ? "#0E3F66" : "#f5f8fc",
                                            color: inputMode === key ? "#fff" : "#0d1b3d",
                                            fontWeight: inputMode === key ? 600 : 400,
                                            fontSize: 14,
                                            cursor: isRecording || isProcessing ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            transition: "background 0.15s, color 0.15s",
                                        }}
                                    >
                                        <Icon style={{ fontSize: 17 }} /> {label}
                                    </button>
                                ))}
                            </div>

                            {/* ── Voice Mode ── */}
                            {inputMode === "voice" && (
                                <div style={{ marginBottom: 22 }}>
                                    {!isRecording && !audioBlob ? (
                                        <div
                                            onClick={!isProcessing ? startRecording : undefined}
                                            style={{
                                                border: "2px dashed #b0c9e0",
                                                borderRadius: 14,
                                                padding: "38px 20px",
                                                textAlign: "center",
                                                cursor: isProcessing ? "not-allowed" : "pointer",
                                                background: "#fafcff",
                                            }}
                                        >
                                            <div style={{
                                                width: 56, height: 56, borderRadius: "50%",
                                                background: "#e8f4fd", display: "flex",
                                                alignItems: "center", justifyContent: "center",
                                                margin: "0 auto 12px", fontSize: 24,
                                            }}>🎙</div>
                                            <Typography fontWeight={600} color="#0d1b3d">Tap to record</Typography>
                                            <Typography variant="body2" color="#888" sx={{ mt: 0.5 }}>
                                                Describe your symptoms in your preferred language
                                            </Typography>
                                        </div>
                                    ) : isRecording ? (
                                        <div style={{
                                            border: "2px dashed #17b6a8",
                                            borderRadius: 14,
                                            padding: "38px 20px",
                                            textAlign: "center",
                                            background: "#f0fdfc",
                                        }}>
                                            <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto 14px" }}>
                                                {/* Pulsing ring */}
                                                <div style={{
                                                    position: "absolute", inset: 0, borderRadius: "50%",
                                                    background: "rgba(198,40,40,0.15)",
                                                    animation: "va-pulse 1.2s ease-in-out infinite",
                                                }} />
                                                {/* Mic circle */}
                                                <div style={{
                                                    position: "absolute", inset: 6, borderRadius: "50%",
                                                    background: "#c62828",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <Mic style={{ fontSize: 22, color: "#fff" }} />
                                                </div>
                                            </div>
                                            <style>{`@keyframes va-pulse{0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(1.25);opacity:1}}`}</style>
                                            <Typography fontWeight={600} color="#0d1b3d">Recording...</Typography>
                                            <Typography variant="body2" color="#888" sx={{ mt: 0.5 }}>
                                                {formatTime(recordingTime)}
                                            </Typography>
                                            <button
                                                onClick={stopRecording}
                                                style={{
                                                    marginTop: 16, background: "#c62828", color: "#fff",
                                                    border: "none", borderRadius: 8, padding: "9px 24px",
                                                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                                                }}
                                            >
                                                ■ Stop Recording
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{
                                            border: "2px dashed #17b6a8",
                                            borderRadius: 14,
                                            padding: "32px 20px",
                                            textAlign: "center",
                                            background: "#f0fdfc",
                                        }}>
                                            <div style={{
                                                width: 56, height: 56, borderRadius: "50%",
                                                background: "#e0f7f5",
                                                border: "2px solid #17b6a8",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                margin: "0 auto 12px",
                                            }}>
                                                <span style={{ fontSize: 26, color: "#17b6a8", fontWeight: 700 }}>✓</span>
                                            </div>
                                            <Typography fontWeight={700} color="#0d1b3d">Recording ready</Typography>
                                            <Typography variant="body2" color="#888" sx={{ mt: 0.5, mb: 2 }}>
                                                Duration {formatTime(recordingTime)}
                                            </Typography>
                                            {audioUrl && (
                                                <audio
                                                    ref={audioPlayerRef}
                                                    src={audioUrl}
                                                    style={{ display: "none" }}
                                                    onEnded={() => setIsPlaying(false)}
                                                />
                                            )}
                                            <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
                                                {audioUrl && (
                                                    <button
                                                        onClick={() => {
                                                            if (!audioPlayerRef.current) return;
                                                            if (isPlaying) {
                                                                audioPlayerRef.current.pause();
                                                                setIsPlaying(false);
                                                            } else {
                                                                audioPlayerRef.current.currentTime = 0;
                                                                audioPlayerRef.current.play();
                                                                setIsPlaying(true);
                                                            }
                                                        }}
                                                        style={{
                                                            border: "1.5px solid #ccc", background: "#fff",
                                                            borderRadius: 8, padding: "7px 18px",
                                                            fontSize: 13, cursor: "pointer",
                                                            display: "flex", alignItems: "center", gap: 5,
                                                        }}
                                                    >
                                                        {isPlaying
                                                            ? <><PauseCircleOutline style={{ fontSize: 16 }} /> Pause</>
                                                            : <><PlayCircleOutline style={{ fontSize: 16 }} /> Play</>
                                                        }
                                                    </button>
                                                )}
                                                <button
                                                    onClick={handleNewRecording}
                                                    style={{
                                                        border: "none", background: "transparent",
                                                        color: "#e53935", fontSize: 13,
                                                        cursor: "pointer", fontWeight: 600,
                                                        display: "flex", alignItems: "center", gap: 5,
                                                    }}
                                                >
                                                    🗑 Re-record
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Text Mode ── */}
                            {inputMode === "text" && (
                                <div style={{ marginBottom: 22 }}>
                                    <Typography variant="subtitle2" fontWeight={600} color="#0d1b3d" sx={{ mb: 1 }}>
                                        Describe your condition
                                    </Typography>
                                    <textarea
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value.slice(0, 1000))}
                                        placeholder="e.g., I've had a sore throat and mild fever since two days, along with headache..."
                                        rows={7}
                                        disabled={isProcessing}
                                        style={{
                                            width: "100%",
                                            border: "1.5px solid #b0c9e0",
                                            borderRadius: 10,
                                            padding: "12px 14px",
                                            fontSize: 14,
                                            resize: "vertical",
                                            outline: "none",
                                            fontFamily: "inherit",
                                            color: "#0d1b3d",
                                            boxSizing: "border-box",
                                            lineHeight: 1.6,
                                            background: isProcessing ? "#f5f5f5" : "#fff",
                                        }}
                                    />
                                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                                        <Typography variant="caption" color="#6b7a99">Minimum 10 characters</Typography>
                                        <Typography variant="caption" color="#6b7a99">{textInput.length} / 1000</Typography>
                                    </div>
                                </div>
                            )}

                            {/* ── Submit Assessment ── */}
                            <button
                                onClick={handleSendAssessment}
                                disabled={isSubmitDisabled}
                                style={{
                                    width: "100%",
                                    background: isSubmitDisabled ? "#8ea8be" : "#0E3F66",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 12,
                                    padding: "14px",
                                    fontSize: 15,
                                    fontWeight: 700,
                                    cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 8,
                                    marginBottom: 16,
                                    transition: "background 0.2s",
                                }}
                            >
                                {isProcessing ? (
                                    <>
                                        <CircularProgress size={18} style={{ color: "#fff" }} />
                                        Processing...
                                    </>
                                ) : (
                                    <> ➤ Submit Assessment</>
                                )}
                            </button>

                            {/* ── Encrypted footer ── */}
                            <div style={{
                                borderTop: "1px solid #eef2f7",
                                paddingTop: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}>
                                <span style={{ color: "#17b6a8", fontSize: 15, flexShrink: 0 }}>🔒</span>
                                <Typography variant="caption" color="#6b7a99">
                                    Encrypted end-to-end. Only your care team can see this.
                                </Typography>
                            </div>
                        </>
                    ) : (
                        <AssessmentResults
                            orgCode={orgCode}
                            appMode={appMode}
                            authToken={authToken}
                            data={assessmentData.data}
                            patient={patient}
                            onNewRecording={handleNewRecording}
                            showDoctors={showDoctors}
                            setShowDoctors={setShowDoctors}
                            onGoToMyAppointments={onGoToMyAppointments}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

const AssessmentResults = ({ data, patient, onNewRecording, showDoctors, setShowDoctors, orgCode, appMode, authToken, onGoToMyAppointments }) => {
    // Set showDoctors if available_doctors exist and showDoctors is not yet set
    React.useEffect(() => {
        if (data && data.available_doctors && Array.isArray(data.available_doctors) && data.available_doctors.length > 0 && !showDoctors && setShowDoctors) {
            setTimeout(() => {
                setShowDoctors(true);
            }, 100);
        }
    }, [data, data?.available_doctors, showDoctors, setShowDoctors]);

    if (!data || typeof data !== 'object') {
        return null;
    }

    return (
        <Box className="assessment-results">
            {/* <Box className="results-header">
                <Typography variant="h6" gutterBottom>
                    Assessment Results
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onNewRecording}
                    sx={{ mb: 2 }}
                >
                    New Assessment
                </Button>
            </Box> */}

            {data.chief_complaints && data.chief_complaints.length > 0 && (
                <Box className="result-section" sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Chief Complaints
                    </Typography>
                    <Box className="chips-container">
                        {data.chief_complaints.map((complaint, index) => (
                            <Chip
                                key={index}
                                label={complaint}
                                color="primary"
                                variant="outlined"
                                sx={{ mr: 1, mb: 1 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {data.recommended_specialization && (
                <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    onClose={() => { }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Recommended Specialization: {data.recommended_specialization}
                    </Typography>
                </Alert>
            )}

            {data.risk_assessment && (
                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Risk Assessment
                    </Typography>
                    {(data.risk_assessment.risk_level || data.risk_assessment.risk_percentage !== undefined) && (
                        <Typography variant="body2" color="text.secondary" textAlign="justify">
                            Risk Level: <strong>{data.risk_assessment.risk_level || "N/A"}</strong>
                            {data.risk_assessment.risk_percentage !== undefined && (
                                <> ({data.risk_assessment.risk_percentage}%)</>
                            )}
                        </Typography>
                    )}
                    {data.risk_assessment.explanation && (
                        <Typography variant="body2" sx={{ mt: 1 }} textAlign="justify">
                            {data.risk_assessment.explanation}
                        </Typography>
                    )}
                </Card>
            )}

            {data.consultation_urgency && data.consultation_urgency.urgency_level && (
                <Alert
                    severity={
                        String(data.consultation_urgency.urgency_level || "").includes("24 hours")
                            ? "warning"
                            : "info"
                    }
                    sx={{ mb: 3 }}
                    onClose={() => { }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Consultation Urgency: {data.consultation_urgency.urgency_level}
                    </Typography>
                    {data.consultation_urgency.reason && (
                        <Typography variant="body2" sx={{ mt: 1 }} textAlign="justify">
                            {data.consultation_urgency.reason}
                        </Typography>
                    )}
                </Alert>
            )}

            {data.home_treatments && data.home_treatments.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Home Treatments
                    </Typography>
                    {data.home_treatments.map((treatment, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="medium" textAlign="justify">
                                {treatment.treatment}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="justify">
                                {treatment.instructions}
                            </Typography>
                        </Box>
                    ))}
                </Card>
            )}

            {data.warning_signs && Array.isArray(data.warning_signs) && data.warning_signs.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => { }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Warning Signs - Seek Immediate Medical Attention
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {data.warning_signs.map((sign, index) => (
                            <li key={index}>
                                <Typography variant="body2" textAlign="justify">{sign}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {showDoctors && data.available_doctors && Array.isArray(data.available_doctors) && data.available_doctors.length > 0 && (
                <Box className="doctors-section">
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        Available Doctors
                    </Typography>
                    <ErrorBoundary>
                        <DoctorSlotsDisplay
                            doctors={data.available_doctors}
                            job_id={data.job_id}
                            onGoToMyAppointments={onGoToMyAppointments}
                            patient={patient}
                            specialization={data.recommended_specialization}
                            orgCode={orgCode}
                            appMode={appMode}
                            authToken={authToken}
                        />
                    </ErrorBoundary>
                </Box>
            )}

            {data.disclaimer && (
                <Alert severity="info" sx={{ mt: 3 }} onClose={() => { }}>
                    <Typography variant="caption" textAlign="justify">{data.disclaimer}</Typography>
                </Alert>
            )}
        </Box>
    );
};

export default VoiceAssistant;

